import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import "@/pages/toto-clay-study.css";

const CANONICAL_ASSET_URL = "/models/toto-canonical.glb";
const REF = { left: 142, top: 138, right: 480, bottom: 505 };

const TOTO_OUTLINE_PX = [
  [180,319],[205,319],[246,319],[276,311],[294,296],[299,268],[298,230],[303,201],
  [303,165],[315,146],[329,151],[345,184],[352,191],[362,184],[381,151],[394,154],[401,188],
  [418,204],[421,226],[448,237],[465,252],[472,270],[470,284],[460,296],[446,304],[427,303],
  [414,296],[402,307],[395,326],[389,337],[382,349],[378,373],[377,408],[378,449],[382,474],
  [391,485],[386,494],[370,497],[355,493],[349,484],[350,455],[351,420],[349,392],[342,377],
  [334,374],[330,394],[330,430],[330,466],[336,482],[330,492],[312,492],[301,486],[300,466],
  [301,444],[297,432],[282,434],[275,446],[275,469],[282,485],[276,492],[257,493],[247,486],
  [246,466],[247,448],[241,438],[225,425],[210,425],[202,443],[201,466],[194,483],[183,490],
  [164,490],[153,482],[155,464],[162,447],[166,429],[158,418],[154,394],[155,365],[159,343],
  [168,328],[180,319],
];
const TAIL_PX = [[164,252],[155,258],[150,278],[153,299],[162,312],[174,321],[188,323],[194,318],[181,313],[171,302],[165,287],[165,272],[171,259]];
const COLLAR_PX = [[293,286],[318,300],[350,314],[378,320],[399,316],[399,335],[377,340],[348,334],[316,320],[292,305]];

function refPoint([px, py]) {
  const w = REF.right - REF.left;
  const h = REF.bottom - REF.top;
  const worldH = 4.0;
  const worldW = worldH * (w / h);
  return new THREE.Vector2(((px - REF.left) / w) * worldW - worldW / 2, ((REF.bottom - py) / h) * worldH);
}

function shapeFromPixels(points) {
  const converted = points.map(refPoint);
  const shape = new THREE.Shape();
  shape.moveTo(converted[0].x, converted[0].y);
  converted.slice(1).forEach((p) => shape.lineTo(p.x, p.y));
  shape.closePath();
  return shape;
}

function mesh(geometry, material, name, position = [0,0,0], scale = [1,1,1], rotation = [0,0,0]) {
  const object = new THREE.Mesh(geometry, material);
  object.name = name;
  object.position.set(...position);
  object.scale.set(...scale);
  object.rotation.set(...rotation);
  object.castShadow = true;
  object.receiveShadow = true;
  return object;
}

function canonicalMaterials(envMap) {
  const body = new THREE.MeshPhysicalMaterial({
    color: 0xf1f5f2,
    roughness: 0.19,
    metalness: 0,
    transmission: 0.58,
    transparent: true,
    opacity: 0.96,
    thickness: 0.84,
    ior: 1.45,
    clearcoat: 0.30,
    clearcoatRoughness: 0.10,
    attenuationColor: new THREE.Color(0xf2f7f3),
    attenuationDistance: 2.2,
    envMap,
    envMapIntensity: 1.22,
  });
  const collar = new THREE.MeshPhysicalMaterial({
    color: 0x6fa6cd,
    roughness: 0.18,
    metalness: 0,
    transmission: 0.05,
    transparent: true,
    opacity: 0.97,
    thickness: 0.16,
    clearcoat: 0.36,
    clearcoatRoughness: 0.10,
    envMap,
    envMapIntensity: 1.16,
  });
  const medal = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    roughness: 0.045,
    metalness: 0.02,
    transmission: 0.92,
    transparent: true,
    opacity: 0.98,
    thickness: 0.5,
    ior: 1.5,
    clearcoat: 0.62,
    clearcoatRoughness: 0.035,
    envMap,
    envMapIntensity: 2.0,
  });
  const detail = new THREE.MeshPhysicalMaterial({
    color: 0x66706c,
    roughness: 0.16,
    metalness: 0.03,
    transmission: 0.10,
    transparent: true,
    opacity: 0.80,
    clearcoat: 0.30,
    envMap,
    envMapIntensity: 1.08,
  });
  return { body, collar, medal, detail };
}

function applyCanonicalMaterials(root, materials) {
  root.traverse((object) => {
    if (!object.isMesh) return;
    object.castShadow = true;
    object.receiveShadow = true;
    const key = `${object.name || ""} ${object.parent?.name || ""}`.toLowerCase();
    if (key.includes("collar")) object.material = materials.collar;
    else if (key.includes("medal") || key.includes("medallion")) object.material = materials.medal;
    else if (key.includes("eye") || key.includes("nose")) object.material = materials.detail;
    else object.material = materials.body;
  });
}

function localDepth([px, py]) {
  if (py < 215) return 0.64;                    // ears stay elegant, never helmet-thick
  if (px > 410 && py < 320) return 0.76;       // muzzle narrows toward the nose
  if (px > 292 && py < 350) return 1.12;       // skull + cheek carry most facial volume
  if (py > 370) return 0.70;                   // short legs remain compact
  if (px < 205 && py < 335) return 0.72;       // rump transition near tail
  return 1.0;                                  // torso/chest
}

function sculptedVolume(points, material, name, depth = 1.12, depthFn = localDepth) {
  const contour = points.map(refPoint);
  const center = contour.reduce((sum, p) => sum.add(p), new THREE.Vector2()).multiplyScalar(1 / contour.length);
  const layers = [
    { t: -1.00, scale: 0.80 },
    { t: -0.70, scale: 0.91 },
    { t: -0.34, scale: 0.975 },
    { t:  0.00, scale: 1.00 },
    { t:  0.34, scale: 0.975 },
    { t:  0.70, scale: 0.91 },
    { t:  1.00, scale: 0.80 },
  ];
  const positions = [];
  const indices = [];
  const layerPoints = [];
  const n = contour.length;

  layers.forEach(({ t, scale }) => {
    const layer = contour.map((p, i) => {
      const q = p.clone().sub(center).multiplyScalar(scale).add(center);
      const z = t * (depth * 0.5) * depthFn(points[i]);
      positions.push(q.x, q.y, z);
      return q;
    });
    layerPoints.push(layer);
  });

  for (let layer = 0; layer < layers.length - 1; layer += 1) {
    const a0 = layer * n;
    const b0 = (layer + 1) * n;
    for (let i = 0; i < n; i += 1) {
      const j = (i + 1) % n;
      indices.push(a0+i, b0+i, b0+j, a0+i, b0+j, a0+j);
    }
  }

  const backFaces = THREE.ShapeUtils.triangulateShape(layerPoints[0], []);
  const frontFaces = THREE.ShapeUtils.triangulateShape(layerPoints[layerPoints.length - 1], []);
  backFaces.forEach(([a,b,c]) => indices.push(c,b,a));
  const frontOffset = (layers.length - 1) * n;
  frontFaces.forEach(([a,b,c]) => indices.push(frontOffset+a, frontOffset+b, frontOffset+c));

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return mesh(geometry, material, name);
}

function buildReferenceMatchedToto(materials) {
  const toto = new THREE.Group();
  toto.name = "TotoReferenceMatchedSculptedVolume";
  toto.add(sculptedVolume(TOTO_OUTLINE_PX, materials.body, "TotoBody", 1.18));

  const tail = sculptedVolume(TAIL_PX, materials.body, "TotoTail", 0.62, () => 0.78);
  tail.position.z = -0.035;
  toto.add(tail);

  const collarGeometry = new THREE.ExtrudeGeometry(shapeFromPixels(COLLAR_PX), {
    depth: 0.13, steps: 1, bevelEnabled: true, bevelSegments: 6, bevelSize: 0.03, bevelThickness: 0.04,
  });
  collarGeometry.translate(0, 0, 0.54);
  toto.add(mesh(collarGeometry, materials.collar, "TotoCollar"));

  const medalAt = refPoint([378,365]);
  toto.add(mesh(new THREE.SphereGeometry(0.24, 36, 28), materials.medal, "TotoMedal", [medalAt.x, medalAt.y, 0.73], [1,1.08,0.54]));
  const medalRingAt = refPoint([373,342]);
  toto.add(mesh(new THREE.TorusGeometry(0.11, 0.024, 12, 30), materials.medal, "TotoMedalRing", [medalRingAt.x, medalRingAt.y, 0.70]));

  const eyeAt = refPoint([407,224]);
  toto.add(mesh(new THREE.SphereGeometry(0.048, 24, 18), materials.detail, "TotoEye", [eyeAt.x, eyeAt.y, 0.66], [1,1,0.62]));
  const noseAt = refPoint([457,272]);
  toto.add(mesh(new THREE.SphereGeometry(0.068, 24, 18), materials.detail, "TotoNose", [noseAt.x, noseAt.y, 0.61], [1.18,0.9,0.76]));

  toto.scale.setScalar(0.90);
  return toto;
}

export default function TotoClayStudy() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xe9e6e1);
    const width = mount.clientWidth;
    const height = mount.clientHeight;
    const aspect = width / height;
    const frustum = 5.72;
    const camera = new THREE.OrthographicCamera((-frustum*aspect)/2, (frustum*aspect)/2, frustum/2, -frustum/2, 0.1, 100);
    camera.position.set(0.68, 2.38, 11.3);
    camera.lookAt(0.04, 1.94, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: "high-performance" });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.35));
    renderer.setSize(width, height, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.06;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mount.appendChild(renderer.domElement);

    const pmrem = new THREE.PMREMGenerator(renderer);
    const environment = pmrem.fromScene(new RoomEnvironment(), 0.035).texture;
    scene.environment = environment;
    const materials = canonicalMaterials(environment);

    scene.add(new THREE.HemisphereLight(0xffffff, 0xc2beb8, 1.48));
    const key = new THREE.DirectionalLight(0xffffff, 2.95);
    key.position.set(4.4, 7.2, 8.0);
    key.castShadow = true;
    key.shadow.mapSize.set(1024,1024);
    key.shadow.camera.left=-6; key.shadow.camera.right=6; key.shadow.camera.top=6; key.shadow.camera.bottom=-6;
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xd9e5e1, 0.92); fill.position.set(-5,3.7,5.4); scene.add(fill);
    const rim = new THREE.DirectionalLight(0xfff0dc, 2.55); rim.position.set(-3.0,5.8,-5.2); scene.add(rim);
    const medalGlint = new THREE.PointLight(0xfff4d3, 3.6, 2.0, 2); medalGlint.position.set(0.72,1.50,2.45); scene.add(medalGlint);

    const tileMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xefede9, roughness: 0.26, metalness: 0, transmission: 0.20, transparent: true, opacity: 0.76,
      thickness: 0.20, ior: 1.33, clearcoat: 0.12, clearcoatRoughness: 0.20, envMap: environment, envMapIntensity: 0.68,
    });
    const tile = mesh(new RoundedBoxGeometry(5.35,5.35,0.15,7,0.38), tileMaterial, "TotoTile", [0,2.15,-1.16]);
    tile.receiveShadow = true;
    scene.add(tile);

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(18,18), new THREE.MeshStandardMaterial({ color:0xe4e0da, roughness:0.95, metalness:0 }));
    floor.rotation.x = -Math.PI/2;
    floor.position.y = -0.08;
    floor.receiveShadow = true;
    scene.add(floor);

    let toto = null;
    let disposed = false;
    const addFallback = () => {
      if (disposed || toto) return;
      toto = buildReferenceMatchedToto(materials);
      scene.add(toto);
    };

    new GLTFLoader().load(CANONICAL_ASSET_URL, (gltf) => {
      if (disposed) return;
      toto = gltf.scene;
      toto.name = "TotoCanonicalGLB";
      applyCanonicalMaterials(toto, materials);
      const box = new THREE.Box3().setFromObject(toto);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const scale = size.y > 0 ? 3.56 / size.y : 1;
      toto.scale.setScalar(scale);
      toto.position.set(-center.x*scale, -box.min.y*scale, -center.z*scale);
      scene.add(toto);
    }, undefined, addFallback);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0.04,1.94,0);
    controls.enableDamping = true;
    controls.dampingFactor = 0.055;
    controls.enablePan = false;
    controls.enableZoom = true;
    controls.minZoom = 0.94;
    controls.maxZoom = 1.42;
    controls.minAzimuthAngle = -0.12;
    controls.maxAzimuthAngle = 0.12;
    controls.minPolarAngle = Math.PI/2 - 0.055;
    controls.maxPolarAngle = Math.PI/2 + 0.055;
    controls.update();

    let frame;
    const render = () => { controls.update(); renderer.render(scene,camera); frame=requestAnimationFrame(render); };
    render();

    const resize = () => {
      const w = mount.clientWidth, h = mount.clientHeight, a = w/h;
      camera.left=(-frustum*a)/2; camera.right=(frustum*a)/2; camera.top=frustum/2; camera.bottom=-frustum/2;
      camera.updateProjectionMatrix(); renderer.setSize(w,h,false);
    };
    const observer = new ResizeObserver(resize); observer.observe(mount);

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      controls.dispose();
      environment.dispose();
      pmrem.dispose();
      renderer.dispose();
      scene.traverse((o) => { if (o.geometry) o.geometry.dispose(); });
      Object.values(materials).forEach((m) => m.dispose());
      tileMaterial.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <main className="toto-clay-study">
      <header className="toto-clay-study__header">
        <div>
          <h1>Sculpted optical volume</h1>
        </div>
        <p className="toto-clay-study__instruction">Drag slightly to inspect volume · scroll to zoom</p>
      </header>
      <section className="toto-clay-study__stage" ref={mountRef} aria-label="Interactive reference-matched Toto sculpted optical volume study" />
      <footer className="toto-clay-study__footer">
        <p><strong>Silhouette locked, depth sculpted.</strong> The approved outline is unchanged; head, muzzle, torso, legs, ears, and tail now carry different optical depth so Toto reads as a cast object instead of a relief.</p>
        <p>The final authored GLB can still drop into the same renderer without changing the visual system.</p>
      </footer>
    </main>
  );
}
